import type { CameraOptions, CanvasEventInfo, Editor } from "./canvas";

const wheelZoomKey = "planroom:mouse-wheel-zoom";
const wheelZoomPreferences = new WeakMap<Editor, boolean>();

export function savedMouseWheelZoom(): boolean {
  try {
    return localStorage.getItem(wheelZoomKey) !== "false";
  } catch {
    return true;
  }
}

export function setMouseWheelZoom(editor: Editor, enabled: boolean) {
  wheelZoomPreferences.set(editor, enabled);
  try {
    localStorage.setItem(wheelZoomKey, String(enabled));
  } catch {}
}

export const boardCameraOptions: Partial<CameraOptions> = {
  wheelBehavior: "zoom",
};

export function installCanvasNavigation(editor: Editor) {
  // Clear the old forced mouse preference. Camera options now own navigation.
  editor.user.updateUserPreferences({ inputMode: null });
  wheelZoomPreferences.set(editor, savedMouseWheelZoom());
  const beforeEvent = (event: CanvasEventInfo) => {
    if (event.type !== "wheel") return;
    // Use the native normalization, cursor anchor and pinch handling. Modified
    // wheel events must still zoom when mouse-wheel zoom is enabled; otherwise
    // they would pan. Resolve per event, after queuing.
    const wheelBehavior =
      !event.ctrlKey && !event.metaKey && wheelZoomPreferences.get(editor)
        ? "zoom"
        : "pan";
    if (editor.getCameraOptions().wheelBehavior !== wheelBehavior)
      editor.setCameraOptions({ wheelBehavior });
  };
  editor.on("before-event", beforeEvent);
  const container = editor.getContainer();
  const view = container.ownerDocument.defaultView!;
  let gesture: {
    pointerId: number;
    x: number;
    y: number;
    camera: ReturnType<Editor["getCamera"]>;
    cursor: ReturnType<Editor["getInstanceState"]>["cursor"];
    moved: boolean;
  } | null = null;
  const stop = (event: Event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  const secondary = (event: MouseEvent) =>
    event.button === 2 ||
    (event.button === 0 &&
      event.ctrlKey &&
      !event.metaKey &&
      /Mac|iPhone|iPad/.test(navigator.platform));
  const down = (event: PointerEvent) => {
    if (!secondary(event)) return;
    const target = event.target as Element;
    if (
      target.closest?.("button,a,input,textarea,select,[contenteditable=true]")
    )
      return;
    stop(event);
    // Capture before shape/selection handlers: secondary drags only change the camera.
    gesture = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      camera: { ...editor.getCamera() },
      cursor: editor.getInstanceState().cursor,
      moved: false,
    };
    editor.setCursor({ type: "grab", rotation: 0 });
    // Pointer capture keeps the drag on the canvas container; keep its cursor
    // in sync for the entire captured drag.
    container.dataset.planroomPanning = "true";
    container.setPointerCapture(event.pointerId);
  };
  const move = (event: PointerEvent) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    stop(event);
    const dx = event.clientX - gesture.x,
      dy = event.clientY - gesture.y;
    if (
      !gesture.moved &&
      dx * dx + dy * dy <= editor.options.dragDistanceSquared
    )
      return;
    gesture.moved = true;
    editor.stopCameraAnimation();
    editor.setCursor({ type: "grabbing", rotation: 0 });
    editor.setCamera(
      {
        x: gesture.camera.x + dx / gesture.camera.z,
        y: gesture.camera.y + dy / gesture.camera.z,
        z: gesture.camera.z,
      },
      { immediate: true },
    );
  };
  const reset = () => {
    if (!gesture) return;
    const current = gesture;
    gesture = null;
    delete container.dataset.planroomPanning;
    if (container.hasPointerCapture(current.pointerId))
      container.releasePointerCapture(current.pointerId);
    editor.setCursor(current.cursor);
  };
  const up = (event: PointerEvent) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    stop(event);
    const moved = gesture.moved;
    reset();
    if (!moved)
      container.dispatchEvent(
        new MouseEvent("contextmenu", {
          bubbles: true,
          cancelable: true,
          button: 2,
          buttons: 0,
          clientX: event.clientX,
          clientY: event.clientY,
        }),
      );
  };
  const cancel = (event: PointerEvent) => {
    if (gesture && event.pointerId === gesture.pointerId) {
      stop(event);
      reset();
    }
  };
  const context = (event: MouseEvent) => {
    // A browser menu can arrive on press or release. Only our stationary-release
    // event reaches the app menus, so a pan cannot open one on any canvas surface.
    if (event.isTrusted && secondary(event)) stop(event);
  };
  container.addEventListener("pointerdown", down, true);
  container.addEventListener("pointermove", move, true);
  container.addEventListener("pointerup", up, true);
  container.addEventListener("pointercancel", cancel, true);
  container.addEventListener("lostpointercapture", reset);
  container.addEventListener("contextmenu", context, true);
  view.addEventListener("blur", reset);
  return () => {
    editor.off("before-event", beforeEvent);
    wheelZoomPreferences.delete(editor);
    reset();
    container.removeEventListener("pointerdown", down, true);
    container.removeEventListener("pointermove", move, true);
    container.removeEventListener("pointerup", up, true);
    container.removeEventListener("pointercancel", cancel, true);
    container.removeEventListener("lostpointercapture", reset);
    container.removeEventListener("contextmenu", context, true);
    view.removeEventListener("blur", reset);
  };
}
